import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  LayoutDashboard, 
  Users, 
  BarChart3, 
  Settings, 
  Shield,
  LogOut,
  CreditCard,
  AlertTriangle,
  Bell,
  Search,
  Menu,
  X,
  ArrowLeft,
  Headphones,
  Bug,
  UserCog,
  Crown,
  HardDrive,
  Handshake,
  Calendar
} from 'lucide-react';
import { useAdmin } from '../OPSDashboard';
import { getBackendURL } from '../../utils/backendURL';
import DashboardOverview from './DashboardOverview';
import UserManagement from './UserManagement';
import AdminUserManagement from './AdminUserManagement';
import SupportManagement from './SupportManagement';
import Analytics from './Analytics';
import Logo from '../Logo';
import SecurityLogs from './SecurityLogs';
import Support from './Support';
import ExecutionDebug from './ExecutionDebug';
import PlanManagement from './PlanManagement';
import DiskCleanup from './DiskCleanup';
import SystemSettings from './SystemSettings';
import PartnersManagement from './PartnersManagement';
import DemoLeadsManagement from './DemoLeadsManagement';

const AdminDashboard = ({ onBack }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [currentView, setCurrentView] = useState('dashboard');
  const [newTicketsCount, setNewTicketsCount] = useState(0);
  const { adminUser, logout } = useAdmin();

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  // Fetch new tickets count
  const fetchNewTicketsCount = async () => {
    try {
      const BACKEND_URL = getBackendURL();
      const token = localStorage.getItem('admin_token');
      
      if (!token) {
        console.warn('No admin token found');
        return;
      }
      
      const response = await axios.get(`${BACKEND_URL}/api/ops/support/tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const tickets = response.data || [];
      console.log('Fetched tickets:', tickets.length);
      
      // Count tickets that need human attention:
      // 1. Status is "needs_review" (bot flagged for human) - ALWAYS COUNT
      // 2. Status is "open" and reply_count is 0 (no replies yet, bot might not have processed) - COUNT
      // 3. Status is "open" and has replies but bot_responded is false (has human/user replies but not bot) - COUNT
      // Don't count: "open" tickets that only have bot replies with high confidence
      const newCount = tickets.filter(t => {
        // Always count tickets flagged for review
        if (t.status === 'needs_review') {
          return true;
        }
        
        // Count open tickets without any replies (waiting for bot or bot hasn't processed yet)
        if (t.status === 'open' && (!t.reply_count || t.reply_count === 0)) {
          return true;
        }
        
        // Count open tickets where bot didn't respond (has human engagement)
        if (t.status === 'open' && t.reply_count > 0 && !t.bot_responded) {
          return true;
        }
        
        // Don't count tickets where bot responded with high confidence (resolved automatically)
        return false;
      }).length;
      
      console.log('Tickets needing human attention:', newCount);
      setNewTicketsCount(newCount);
    } catch (error) {
      console.error('Error fetching new tickets count:', error);
    }
  };

  // Fetch new tickets count on mount and every 2 minutes
  useEffect(() => {
    fetchNewTicketsCount();
    const timer = setInterval(fetchNewTicketsCount, 120000); // Every 2 minutes
    return () => clearInterval(timer);
  }, []);

  const navigation = React.useMemo(() => [
    {
      name: 'Dashboard',
      key: 'dashboard',
      icon: LayoutDashboard,
      current: currentView === 'dashboard'
    },
    {
      name: 'User Management',
      key: 'users',
      icon: Users,
      current: currentView === 'users'
    },
    {
      name: 'Admin Users',
      key: 'admin-users',
      icon: UserCog,
      current: currentView === 'admin-users',
      superAdminOnly: true  // Only show for super admins
    },
    {
      name: 'Analytics',
      key: 'analytics',
      icon: BarChart3,
      current: currentView === 'analytics'
    },
    {
      name: 'Support',
      key: 'support',
      icon: Headphones,
      current: currentView === 'support',
      badge: newTicketsCount > 0 ? newTicketsCount : null
    },
    {
      name: 'Plans',
      key: 'plans',
      icon: Crown,
      current: currentView === 'plans'
    },
    {
      name: 'Partners',
      key: 'partners',
      icon: Handshake,
      current: currentView === 'partners'
    },
    {
      name: 'Demo / Leads',
      key: 'demo-leads',
      icon: Calendar,
      current: currentView === 'demo-leads'
    },
    {
      name: 'Disk Cleanup',
      key: 'cleanup',
      icon: HardDrive,
      current: currentView === 'cleanup'
    },
    {
      name: 'Debug',
      key: 'debug',
      icon: Bug,
      current: currentView === 'debug'
    },
    {
      name: 'Security Logs',
      key: 'security',
      icon: Shield,
      current: currentView === 'security'
    },
    {
      name: 'System Settings',
      key: 'system-settings',
      icon: Settings,
      current: currentView === 'system-settings',
      superAdminOnly: true
    }
  ], [currentView, newTicketsCount]);

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
      // The parent will handle navigation after logout
    }
  };

  const handleNavigation = (viewKey) => {
    setCurrentView(viewKey);
    setSidebarOpen(false); // Close mobile sidebar
    
    // Refresh ticket count when navigating away from support
    if (viewKey !== 'support') {
      fetchNewTicketsCount();
    }
  };

  const getRoleColor = (role) => {
    const colors = {
      super_admin: 'bg-purple-100 text-purple-800',
      admin: 'bg-blue-100 text-blue-800',
      support: 'bg-green-100 text-green-800',
      read_only: 'bg-gray-100 text-gray-800'
    };
    return colors[role] || colors.read_only;
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case 'users':
        return <UserManagement />;
      case 'admin-users':
        return <AdminUserManagement />;
      case 'analytics':
        return <Analytics />;
      case 'support':
        return <SupportManagement />;
      case 'plans':
        return <PlanManagement />;
      case 'partners':
        return <PartnersManagement />;
      case 'demo-leads':
        return <DemoLeadsManagement />;
      case 'cleanup':
        return <DiskCleanup />;
      case 'debug':
        return <ExecutionDebug />;
      case 'security':
        return <SecurityLogs />;
      case 'system-settings':
        return <SystemSettings />;
      case 'dashboard':
      default:
        return <DashboardOverview onNavigate={handleNavigation} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black bg-opacity-25 lg:hidden" 
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:fixed lg:left-0 lg:top-0 lg:bottom-0 flex flex-col`}>
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center">
            <Logo size="md" showText={false} />
            <div className="ml-3">
              <h1 className="text-lg font-semibold text-gray-900">Admin OPS</h1>
            </div>
          </div>
          <div className="lg:hidden">
            <button
              onClick={() => setSidebarOpen(false)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Navigation - Scrollable Content */}
        <nav className="flex-1 overflow-y-auto mt-6 px-3 pb-4">
          <div className="space-y-1">
            {navigation
              .filter(item => !item.superAdminOnly || adminUser?.role === 'super_admin')
              .map((item) => (
              <button
                key={item.key}
                onClick={() => handleNavigation(item.key)}
                className={`${
                  item.current
                    ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                    : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                } group flex items-center justify-between w-full px-3 py-2 text-sm font-medium border-l-4 rounded-md transition-colors duration-150`}
              >
                <div className="flex items-center">
                  <item.icon
                    className={`${
                      item.current ? 'text-indigo-500' : 'text-gray-400 group-hover:text-gray-500'
                    } mr-3 h-5 w-5`}
                  />
                  {item.name}
                </div>
                {item.badge !== undefined && item.badge !== null && (
                  <span className="ml-auto inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-600 rounded-full">
                    {item.badge}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Back to Main App */}
          {onBack && (
            <div className="mt-8 pt-6 border-t border-gray-200">
              <button
                onClick={onBack}
                className="group flex items-center w-full px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-md transition-colors duration-150"
              >
                <ArrowLeft className="text-gray-400 group-hover:text-gray-500 mr-3 h-5 w-5" />
                Back to Dashboard
              </button>
            </div>
          )}

        </nav>

        {/* User info and logout - Fixed at bottom */}
        <div className="flex-shrink-0 p-4 border-t border-gray-200 bg-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 rounded-full bg-indigo-600 flex items-center justify-center">
                    <span className="text-sm font-medium text-white">
                      {adminUser?.email?.charAt(0).toUpperCase() || 'A'}
                    </span>
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-700">
                    {adminUser?.email || 'Admin'}
                  </p>
                  <p className="text-xs text-gray-500">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getRoleColor(adminUser?.role)}`}>
                      {adminUser?.role || 'admin'}
                    </span>
                  </p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="ml-3 inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
                title="Logout"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden lg:ml-64">
        {/* Top navigation */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
              >
                <Menu className="h-6 w-6" />
              </button>
              <div className="lg:ml-0 ml-4">
                <h1 className="text-2xl font-semibold text-gray-900">
                  {navigation.find(item => item.current)?.name || 'Dashboard'}
                </h1>
              </div>
            </div>

            {/* Right side of header */}
            <div className="flex items-center space-x-4">
              {/* Time display */}
              <div className="hidden sm:block text-sm text-gray-500">
                {currentTime.toLocaleString()}
              </div>

              {/* Status indicator */}
              <div className="flex items-center space-x-2">
                <div className="h-2 w-2 bg-green-400 rounded-full"></div>
                <span className="text-sm text-gray-600">System Online</span>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto bg-gray-50">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              {renderCurrentView()}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;